import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useAuth } from '../services/authProvider'
import { getLevelSuffix } from '../utils/roleLabels'
import { leaveSourceLabel } from '../utils/leaveSource'

const LEAVE_TYPES = {
  sick:     { label: 'ลาป่วย', bg: 'warning' },
  personal: { label: 'ลากิจ',  bg: 'info' },
}

const fmtDateTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'

const fmtDateTimeTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const todayLocal = () => new Date().toLocaleDateString('en-CA')

// การ์ดนักเรียนที่ลา 1 รายการ สำหรับมุมมองมือถือ
const StudentLeaveCard = ({ l }) => {
  const type = LEAVE_TYPES[l.leave_type] || { label: l.leave_type, bg: 'secondary' }
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="fw-semibold">{l.st_name}</div>
        <Badge bg={type.bg} text={type.bg === 'warning' ? 'dark' : undefined}>{type.label}</Badge>
      </div>
      <div className="fs-xs text-muted">{l.c_class}/{l.r_no} · {l.st_idnumber}</div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div>
          {l.leave_start === l.leave_end
            ? fmtDateTH(l.leave_start)
            : `${fmtDateTH(l.leave_start)} - ${fmtDateTH(l.leave_end)}`}
          <span className="text-muted ms-1">({l.leave_days} วัน)</span>
        </div>
        {l.leave_note && <div><span className="text-muted">หมายเหตุ:</span> {l.leave_note}</div>}
        <div className="text-muted fs-xs mt-1">
          {l.reported_by || '-'} ({leaveSourceLabel(l.source)}) · แจ้งเมื่อ {fmtDateTimeTH(l.created_at)}
        </div>
      </div>
    </div>
  )
}

// รายงานอ่านอย่างเดียวสำหรับหัวหน้าวิชาการสามัญ — นักเรียนที่ลา
// รวมการแจ้งลาจากทุกฝ่าย (ครูประจำชั้น / ฝ่ายปกครอง / หัวหน้าวิชาการ) โดยคอลัมน์
// "ผู้แจ้ง" บอกที่มาจาก sl.source; แจ้งลาเองได้ที่หน้า /academic/student-leave
export default function StudentsOnLeave() {
  const { posIds } = useAuth()
  const levelSuffix = getLevelSuffix(posIds)
  const [startDate, setStartDate] = useState(todayLocal())
  const [endDate, setEndDate] = useState(todayLocal())
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [roomFilter, setRoomFilter] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchData = useCallback(async () => {
    if (startDate > endDate) return
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/getStudentLeaveReport/${startDate}/${endDate}`)
      setLeaves(res.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchData() }, [fetchData])

  const roomOptions = useMemo(() => {
    const seen = new Map()
    leaves.forEach(l => seen.set(`${l.c_id}/${l.r_no}`, `${l.c_class}/${l.r_no}`))
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
  }, [leaves])

  const filtered = useMemo(() => leaves.filter(l =>
    (!typeFilter || l.leave_type === typeFilter) &&
    (!roomFilter || `${l.c_id}/${l.r_no}` === roomFilter)
  ), [leaves, typeFilter, roomFilter])

  const studentCount = useMemo(() => new Set(filtered.map(l => l.st_id)).size, [filtered])
  const sickCount = filtered.filter(l => l.leave_type === 'sick').length
  const personalCount = filtered.filter(l => l.leave_type === 'personal').length
  const singleDay = startDate === endDate

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-user-unfollow-line me-2 text-primary" />
              นักเรียนที่ลา{levelSuffix}
            </h4>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 170 }}
                value={startDate}
                max={endDate}
                onChange={e => e.target.value && setStartDate(e.target.value)}
              />
              <span className="text-muted">ถึง</span>
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 170 }}
                value={endDate}
                min={startDate}
                onChange={e => e.target.value && setEndDate(e.target.value)}
              />
              <Button size="sm" variant="outline-secondary" onClick={() => { setStartDate(todayLocal()); setEndDate(todayLocal()) }}>
                วันนี้
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              <Alert variant={filtered.length === 0 ? 'success' : 'warning'} className="py-2">
                <b>{singleDay ? `วันที่ ${fmtDateTH(startDate)}` : `${fmtDateTH(startDate)} - ${fmtDateTH(endDate)}`}</b> —{' '}
                {filtered.length === 0
                  ? 'ไม่มีนักเรียนแจ้งลาในช่วงนี้'
                  : <>นักเรียนลา <b>{studentCount}</b> คน ({filtered.length} รายการ) ·{' '}
                      <Badge bg="warning" text="dark">ลาป่วย {sickCount}</Badge>{' '}
                      <Badge bg="info">ลากิจ {personalCount}</Badge>
                    </>}
              </Alert>

              <Card className="card-one">
                <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">รายการลาที่แจ้งไว้</Card.Title>
                  <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
                    <Form.Select size="sm" style={{ width: 150 }} value={roomFilter} onChange={e => setRoomFilter(e.target.value)}>
                      <option value="">ทุกห้อง</option>
                      {roomOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                    <Form.Select size="sm" style={{ width: 140 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                      <option value="">ทุกประเภท</option>
                      <option value="sick">ลาป่วย</option>
                      <option value="personal">ลากิจ</option>
                    </Form.Select>
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {filtered.length === 0 ? (
                    <div className="text-center text-muted py-5">ไม่พบรายการลา</div>
                  ) : view === 'card' ? (
                    <div className="p-3">
                      <Row className="g-3">
                        {filtered.map(l => (
                          <Col key={l.leave_id} xs={12} md={6} xl={4}>
                            <StudentLeaveCard l={l} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ) : (
                    <Table responsive hover className="mb-0 fs-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 90 }}>ห้อง</th>
                          <th style={{ width: 110 }}>เลขประจำตัว</th>
                          <th>ชื่อ - สกุล</th>
                          <th style={{ width: 100 }}>ประเภท</th>
                          <th style={{ width: 220 }}>วันที่ลา</th>
                          <th>หมายเหตุ</th>
                          <th style={{ width: 200 }}>ผู้แจ้ง</th>
                          <th style={{ width: 130 }}>แจ้งเมื่อ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(l => {
                          const type = LEAVE_TYPES[l.leave_type] || { label: l.leave_type, bg: 'secondary' }
                          return (
                            <tr key={l.leave_id}>
                              <td>{l.c_class}/{l.r_no}</td>
                              <td>{l.st_idnumber}</td>
                              <td>{l.st_name}</td>
                              <td><Badge bg={type.bg} text={type.bg === 'warning' ? 'dark' : undefined}>{type.label}</Badge></td>
                              <td>
                                {l.leave_start === l.leave_end
                                  ? fmtDateTH(l.leave_start)
                                  : `${fmtDateTH(l.leave_start)} - ${fmtDateTH(l.leave_end)}`}
                                <span className="text-muted ms-1">({l.leave_days} วัน)</span>
                              </td>
                              <td>{l.leave_note || <span className="text-muted">-</span>}</td>
                              <td>
                                {l.reported_by || <span className="text-muted">-</span>}
                                <div className="fs-xs text-muted">{leaveSourceLabel(l.source)}</div>
                              </td>
                              <td className="text-muted">{fmtDateTimeTH(l.created_at)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
